// Replays an mjai event stream and reports invariant violations. Used by the
// parser test suites (assert no violations across all example fixtures) and by
// the UI as a soft warning channel. Focuses on the invariants that catch
// converter bugs: call events must match the target's last discard, consumed
// tiles must come from the caller's hand, and scores must be self-consistent.

const TILE_RE = /^([1-9][mps]|5[mps]r|[ESWNPFC])$/;

interface PonMeld {
    kind: string; // rank+suit with red flag stripped, e.g. "5p"
}

export function validateEvents(events: MjaiEvent[]): string[] {
    const problems: string[] = [];
    let hands: string[][] = [];
    let pons: PonMeld[][] = [];
    let masked: boolean[] = [];
    let lastDiscard: { actor: number; pai: string } | null = null;
    let running: number[] | null = null;
    let inKyoku = false;
    let sawEnd: "hora" | "ryukyoku" | null = null;

    const report = (i: number, ev: MjaiEvent, msg: string) => {
        problems.push(`event ${i} (${ev.type}): ${msg}`);
    };

    const stripRed = (pai: string) => pai.replace(/^5([mps])r$/, "5$1");

    const removeFromHand = (i: number, ev: MjaiEvent, actor: number, tiles: string[]) => {
        if (masked[actor]) return;
        const hand = hands[actor];
        if (!hand) return;
        for (const t of tiles) {
            const idx = hand.indexOf(t);
            if (idx === -1) {
                report(i, ev, `tile ${t} not in hand of player ${actor}`);
            } else {
                hand.splice(idx, 1);
            }
        }
    };

    for (let i = 0; i < events.length; i++) {
        const ev = events[i] as MjaiEvent;
        const actor = typeof ev.actor === "number" ? ev.actor : -1;
        switch (ev.type) {
            case "start_game":
                if (i !== 0) report(i, ev, "start_game is not the first event");
                break;
            case "start_kyoku": {
                if (inKyoku) report(i, ev, "previous kyoku not closed by end_kyoku");
                inKyoku = true;
                sawEnd = null;
                lastDiscard = null;
                const tehais = ev.tehais as string[][] | undefined;
                const scores = ev.scores as number[] | undefined;
                if (!Array.isArray(scores)) {
                    report(i, ev, "missing scores (required by the viewer)");
                } else if (running && scores.some((s, p) => s !== running?.[p])) {
                    report(i, ev, `scores [${scores}] != carried-over scores [${running}]`);
                }
                if (scores) running = [...scores];
                if (!Array.isArray(tehais)) {
                    report(i, ev, "missing tehais");
                    hands = [];
                    break;
                }
                hands = tehais.map((h) => [...h]);
                pons = tehais.map(() => []);
                masked = tehais.map((h) => h.some((t) => t === "?"));
                for (const [p, hand] of hands.entries()) {
                    if (hand.length !== 13)
                        report(i, ev, `player ${p} tehai has ${hand.length} tiles`);
                    if (!masked[p]) {
                        for (const t of hand) {
                            if (!TILE_RE.test(t))
                                report(i, ev, `bad tile ${t} in tehai of player ${p}`);
                        }
                    }
                }
                break;
            }
            case "tsumo":
                if (!inKyoku) report(i, ev, "outside a kyoku");
                if (ev.pai !== "?" && !TILE_RE.test(ev.pai as string)) {
                    report(i, ev, `bad tile ${ev.pai}`);
                }
                if (ev.pai === "?" && actor >= 0) masked[actor] = true;
                if (actor >= 0 && !masked[actor]) hands[actor]?.push(ev.pai as string);
                break;
            case "dahai":
                if (!TILE_RE.test(ev.pai as string)) report(i, ev, `bad tile ${ev.pai}`);
                removeFromHand(i, ev, actor, [ev.pai as string]);
                lastDiscard = { actor, pai: ev.pai as string };
                break;
            case "chi":
            case "pon":
            case "daiminkan": {
                if (!lastDiscard) {
                    report(i, ev, "call with no preceding discard");
                } else {
                    if (ev.target !== lastDiscard.actor) {
                        report(i, ev, `target ${ev.target} != last discarder ${lastDiscard.actor}`);
                    }
                    if (ev.pai !== lastDiscard.pai) {
                        report(i, ev, `pai ${ev.pai} != last discard ${lastDiscard.pai}`);
                    }
                }
                removeFromHand(i, ev, actor, (ev.consumed as string[]) ?? []);
                if (ev.type === "pon" && actor >= 0) {
                    pons[actor]?.push({ kind: stripRed(ev.pai as string) });
                }
                lastDiscard = null;
                break;
            }
            case "ankan":
                if (!Array.isArray(ev.consumed) || ev.consumed.length !== 4) {
                    report(i, ev, "ankan must consume exactly 4 tiles");
                }
                removeFromHand(i, ev, actor, (ev.consumed as string[]) ?? []);
                break;
            case "kakan": {
                removeFromHand(i, ev, actor, [ev.pai as string]);
                const kind = stripRed(ev.pai as string);
                const ponList = pons[actor];
                if (ponList && !masked[actor] && !ponList.some((m) => m.kind === kind)) {
                    report(i, ev, `kakan of ${ev.pai} without a matching pon`);
                }
                break;
            }
            case "kita":
                removeFromHand(i, ev, actor, ["N"]);
                break;
            case "reach_accepted":
                if (running && actor >= 0) {
                    running[actor] = (running[actor] as number) - 1000;
                }
                break;
            case "hora":
            case "ryukyoku": {
                if (!inKyoku) report(i, ev, "outside a kyoku");
                sawEnd = ev.type;
                const deltas = ev.deltas as number[] | undefined;
                const scores = ev.scores as number[] | undefined;
                if (running && Array.isArray(deltas)) {
                    const expected: number[] = running.map((s, p) => s + (deltas[p] ?? 0));
                    if (Array.isArray(scores) && scores.some((s, p) => s !== expected[p])) {
                        report(i, ev, `scores [${scores}] != running+deltas [${expected}]`);
                    }
                    running = Array.isArray(scores) ? [...scores] : expected;
                } else if (Array.isArray(scores)) {
                    running = [...scores];
                }
                break;
            }
            case "end_kyoku":
                if (!sawEnd) report(i, ev, "end_kyoku without hora/ryukyoku");
                inKyoku = false;
                break;
            case "end_game":
                if (i !== events.length - 1) report(i, ev, "end_game is not the last event");
                break;
            case "reach":
            case "dora":
                if (!inKyoku) report(i, ev, "outside a kyoku");
                break;
            default:
                report(i, ev, `unknown event type "${ev.type}"`);
        }
    }
    if (inKyoku) problems.push("log ends inside a kyoku (missing end_kyoku)");
    return problems;
}
